<template>
  <div class="relative">
    <TwnBaseBtn
      v-if="hasTrigger"
      :aria-expanded="isOpen"
      aria-haspopup="dialog"
      aria-label="EXO fiscalisation setup"
      icon="receipt_long"
      label="EXO"
      severity="secondary"
      size="small"
      variant="outlined"
      @click="togglePopover"
    />

    <TwnPopover
      ref="popoverRef"
      @hide="handleHide"
      @show="handleShow"
    >
      <!-- Bounded to the viewport so the panel still fits at 320px, where a fixed 20rem would clip. -->
      <div class="flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-4">
        <header class="flex flex-col gap-0.5">
          <p class="text-sm font-medium text-surface-900">EXO fiscalisation</p>
          <p class="text-xs text-surface-500">Malta software fiscalisation, by scope.</p>
        </header>

        <!-- Live region: the panel opens empty and fills in, which is otherwise silent to a screen reader. -->
        <div
          aria-live="polite"
          class="flex flex-col gap-4"
          role="status"
        >
          <div
            v-if="isLoading"
            aria-label="Loading EXO setup"
            class="flex flex-col gap-3"
          >
            <TwnSkeleton
              v-for="line in 4"
              :key="line"
              height="1rem"
            />
          </div>

          <TwnMessage
            v-else-if="isError"
            :closable="false"
            icon="error"
            severity="error"
          >
            Couldn't load the EXO setup. Reload the page to try again.
          </TwnMessage>

          <template v-else-if="setup">
            <div class="flex items-center justify-between gap-4">
              <span class="text-sm text-surface-500">Tenant</span>
              <TwnIndicator
                :label="statusLabel(setup.tenantStatus)"
                :severity="statusSeverity(setup.tenantStatus)"
                size="small"
              />
            </div>

            <div class="flex flex-col gap-1.5 border-t border-surface-100 pt-3">
              <p class="text-xs font-medium tracking-wide text-surface-500 uppercase">
                {{ locationsHeading }}
              </p>

              <!-- overscroll-contain stops the scroll chaining to the page behind the popover. -->
              <dl
                v-if="setup.rows.length"
                class="max-h-64 divide-y divide-surface-100 overflow-y-auto overscroll-contain"
              >
                <div
                  v-for="row in setup.rows"
                  :key="row.key"
                  class="flex items-start justify-between gap-3 py-2"
                >
                  <dt class="flex min-w-0 flex-col">
                    <span class="text-sm break-words text-surface-900">{{ row.name }}</span>
                    <span
                      v-if="row.isInherited"
                      class="text-xs text-surface-400"
                      >Inherited from tenant</span
                    >
                  </dt>
                  <dd class="shrink-0">
                    <TwnIndicator
                      :label="statusLabel(row.status)"
                      :severity="statusSeverity(row.status)"
                      size="small"
                    />
                  </dd>
                </div>
              </dl>

              <p
                v-else
                class="py-2 text-sm text-surface-500"
              >
                No locations yet.
              </p>

              <p
                v-if="setup.isTruncated"
                class="pt-1 text-xs text-surface-500"
              >
                Showing {{ setup.rows.length }} of {{ setup.overrideTotal }} locations that set their own status.
                Locations that inherit aren't listed.
              </p>
            </div>
          </template>
        </div>
      </div>
    </TwnPopover>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, useTemplateRef, watch } from 'vue'

import type { TwnExoStatusIndicatorProps } from '@/domains/fiscal/components/molecules/twn-exo-status-indicator/TwnExoStatusIndicator.types'
import type { ExoStatus } from '@/domains/fiscal/types/consts/ExoStatus.consts'

import TwnIndicator from '@/components/atoms/badges/twn-indicator/TwnIndicator.vue'
import TwnBaseBtn from '@/components/atoms/buttons/twn-base-btn/TwnBaseBtn.vue'
import TwnMessage from '@/components/atoms/messages/twn-message/TwnMessage.vue'
import TwnPopover from '@/components/atoms/prime/popover/twn-popover/TwnPopover.vue'
import TwnSkeleton from '@/components/atoms/prime/skeleton/twn-skeleton/TwnSkeleton.vue'
import { useExoSetup } from '@/domains/fiscal/composables/use-exo-setup/useExoSetup'
import { EXO_STATUS_LABELS, EXO_STATUS_SEVERITIES } from '@/domains/fiscal/types/consts/ExoStatus.consts'

// Without its button, a caller such as the sidebar opens the panel through `open`.
withDefaults(defineProps<TwnExoStatusIndicatorProps>(), { hasTrigger: true })

const popoverRef = useTemplateRef<InstanceType<typeof TwnPopover>>('popoverRef')

const isOpen = ref(false)

// Latches on first open and stays true: the queries are keyed and cached, so keeping them enabled
// lets a reopen serve from cache instead of re-running as the `enabled` flag flaps.
const hasOpened = ref(false)

const { setup, isLoading, isError } = useExoSetup(hasOpened)

// The panel is placed while it holds the skeleton; once the rows land it is taller, so place it again.
watch(
  setup,
  () => {
    if (isOpen.value) popoverRef.value?.align()
  },
  { flush: 'post' }
)

const locationsHeading = computed(() => (setup.value?.isTruncated ? 'Locations with their own status' : 'Locations'))

// `isOpen` follows the popover's own events rather than the click, so Escape and outside-click
// dismissals keep aria-expanded honest.
const togglePopover = (event: Event) => {
  hasOpened.value = true
  popoverRef.value?.toggle(event)
}

const open = (event: Event) => {
  hasOpened.value = true
  popoverRef.value?.show(event)
}

const handleShow = () => {
  isOpen.value = true
}

const handleHide = () => {
  isOpen.value = false
}

const statusLabel = (status: ExoStatus | null) => (status ? EXO_STATUS_LABELS[status] : 'Not set')

const statusSeverity = (status: ExoStatus | null) => (status ? EXO_STATUS_SEVERITIES[status] : 'secondary')

defineExpose({ open })
</script>
